// src/lib/employeesApi.js
import { db, auth } from "../firebase";
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    onSnapshot,
    query,
    where,
    getDocs,
    getDoc,
    limit,
} from "firebase/firestore";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
} from "firebase/auth";

/**
 * Employees collection path:
 * services (collection) -> jobs (document) -> collectiondata (collection)
 */
export const employeesCol = collection(db, "services", "jobs", "collectiondata");

/* -----------------------------------------------------------------------------
 * LISTEN / QUERY HELPERS
 * ---------------------------------------------------------------------------*/

/**
 * Subscribe to employees with optional filters.
 * Filters: status, categoryKey, subcategoryKey
 * (categoryKey/subcategoryKey refer to the PRIMARY job on the doc)
 */
export const subscribeEmployees = (
    onData,
    onError,
    { status, categoryKey, subcategoryKey } = {}
) => {
    let qRef = employeesCol;

    if (status) qRef = query(qRef, where("status", "==", status));
    if (categoryKey) qRef = query(qRef, where("categoryKey", "==", categoryKey));
    if (subcategoryKey) qRef = query(qRef, where("subcategoryKey", "==", subcategoryKey));

    return onSnapshot(
        qRef,
        (snap) => {
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            // Sort by createdAt ascending
            items.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
            onData(items);
        },
        (err) => onError?.(err)
    );
};

/** Subscribe to a single employee by document id. */
export const subscribeEmployeeById = (id, onData, onError) => {
    if (!id) {
        onError?.(new Error("Missing employee document id"));
        return () => { };
    }
    const ref = doc(employeesCol, id);
    return onSnapshot(
        ref,
        (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
        (err) => onError?.(err)
    );
};

/** Subscribe to employee by Auth UID. Returns first match. */
export const subscribeEmployeeByUid = (uid, onData, onError) => {
    if (!uid) {
        onError?.(new Error("Missing uid"));
        return () => { };
    }
    const qRef = query(employeesCol, where("uid", "==", uid), limit(1));
    return onSnapshot(
        qRef,
        (snap) => {
            const d = snap.docs[0];
            onData(d ? { id: d.id, ...d.data() } : null);
        },
        (err) => onError?.(err)
    );
};

/** One-time fetch by doc id. */
export const getEmployeeById = async (id) => {
    if (!id) return null;
    const ref = doc(employeesCol, id);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** One-time fetch by uid. */
export const getEmployeeByUid = async (uid) => {
    if (!uid) return null;
    const qRef = query(employeesCol, where("uid", "==", uid), limit(1));
    const snap = await getDocs(qRef);
    const d = snap.docs[0];
    return d ? { id: d.id, ...d.data() } : null;
};

/* -----------------------------------------------------------------------------
 * EMPLOYEE ID GENERATION
 * ---------------------------------------------------------------------------*/

/** Generate unique 8-digit employee ID (tries up to 20 times). */
export const generateUniqueEmpId = async () => {
    const tries = 20;
    for (let i = 0; i < tries; i++) {
        const num = Math.floor(10000000 + Math.random() * 90000000); // 8-digit
        const dup = await getDocs(query(employeesCol, where("empId", "==", num)));
        if (dup.empty) return num;
    }
    throw new Error("Failed to generate a unique Employee ID. Please retry.");
};

/* -----------------------------------------------------------------------------
 * UTIL
 * ---------------------------------------------------------------------------*/

/** Compute age in whole years from 'YYYY-MM-DD' string; returns null if invalid. */
export const computeAgeYears = (dobStr) => {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) {
        age--;
    }
    return Math.max(age, 0);
};

/* -----------------------------------------------------------------------------
 * CREATE / UPDATE / DELETE
 * ---------------------------------------------------------------------------*/

/**
 * Create employee (Admin)
 * Optionally create an Auth user for email+password (createAuth=true).
 * If the email is already in use:
 *  - we try signInWithEmailAndPassword(email, password) to attach uid
 *  - if that fails, we continue without uid (and log a note)
 */
export const createEmployeeAdmin = async (payload, { createAuth = false } = {}) => {
    if (!payload || typeof payload !== "object") {
        throw new TypeError(
            "createEmployeeAdmin requires a payload object, but received: " + String(payload)
        );
    }

    // Required fields
    const required = ["firstName", "mobile"];
    const missing = required.filter((k) => !payload[k]);
    if (missing.length) {
        throw new Error(`Missing required fields: ${missing.join(", ")}`);
    }

    // Prepare primary job from either explicit categoryKey/subcategoryKey or jobs[0]
    const jobsArray = Array.isArray(payload.jobs) ? payload.jobs : [];
    const primaryJob = jobsArray[0] || {
        title: payload.jobTitle || "",
        categoryKey: payload.categoryKey || null,
        subcategoryKey: payload.subcategoryKey || null,
        experienceYears: payload.experienceYears ?? null,
    };

    // Normalize jobs: only keep up to 20 valid rows (any value present)
    const jobs = jobsArray
        .map((j) => ({
            title: String(j.title || "").trim(),
            categoryKey: j.categoryKey || null,
            subcategoryKey: j.subcategoryKey || null,
            experienceYears:
                typeof j.experienceYears === "number" ? j.experienceYears : null,
        }))
        .filter(
            (j) =>
                j.title ||
                j.categoryKey ||
                j.subcategoryKey ||
                (j.experienceYears !== null && j.experienceYears !== undefined)
        )
        .slice(0, 20);

    const empId = payload.empId || (await generateUniqueEmpId());

    // Auth handling
    let userUid = payload.uid || null;
    const email = payload.email || null;
    const password = payload.password || null;

    if (createAuth && email && password && !userUid) {
        try {
            const cred = await createUserWithEmailAndPassword(auth, email, password);
            userUid = cred.user.uid;
        } catch (e) {
            // If email already in use, try sign-in to get uid; else continue without uid
            if (e?.code === "auth/email-already-in-use") {
                try {
                    const cred = await signInWithEmailAndPassword(auth, email, password);
                    userUid = cred.user.uid;
                    // NOTE: If wrong password, we proceed without uid.
                } catch (e2) {
                    console.warn(
                        "[employeesApi] Email already in use but sign-in failed. Continuing without uid.",
                        e2?.code || e2
                    );
                }
            } else {
                console.warn("[employeesApi] Auth creation failed, continuing without uid:", e?.code || e);
            }
        }
    }

    // Personal & profile fields
    const dob = payload.dateOfBirth || null; // 'YYYY-MM-DD'
    const ageYears =
        typeof payload.ageYears === "number" ? payload.ageYears : computeAgeYears(dob);

    const docPayload = {
        uid: userUid || null,
        empId,

        // Personal
        firstName: payload.firstName || "",
        lastName: payload.lastName || "",
        fatherName: payload.fatherName || "",
        address: payload.address || "",
        mobile: payload.mobile || "",
        aadhaar: payload.aadhaar || "",
        pan: payload.pan || "",
        ids: Array.isArray(payload.ids) ? payload.ids.slice(0, 10) : [], // [{type, value}]
        email,

        // DOB / Age / Experience
        dateOfBirth: dob, // store as ISO string 'YYYY-MM-DD' for simplicity
        ageYears: ageYears ?? null,
        experienceYears:
            typeof payload.experienceYears === "number" ? payload.experienceYears : null,

        // Jobs (multiple)
        jobs,

        // Primary job fields for filtering/grouping
        categoryKey: primaryJob.categoryKey || null,
        subcategoryKey: primaryJob.subcategoryKey || null,

        // Optional skills/bio/availability
        skills: Array.isArray(payload.skills) ? payload.skills : [],
        bio: payload.bio || "",
        availability: payload.availability || "full-time", // 'full-time' | 'part-time' | 'contract' | 'on-call'

        status: payload.status || "pending", // default pending
        role: "employee",

        createdBy: auth?.currentUser?.uid || "unknown",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };

    try {
        const ref = await addDoc(employeesCol, docPayload);
        console.log("Employee created at id:", ref.id);
        return ref;
    } catch (e) {
        console.error("createEmployeeAdmin Firestore error:", e);
        throw e;
    }
};

/** Create employee (Public registration) — always pending. */
export const createEmployeePublic = async (payload) => {
    const empId = await generateUniqueEmpId();

    const dob = payload.dateOfBirth || null;
    const ageYears =
        typeof payload.ageYears === "number" ? payload.ageYears : computeAgeYears(dob);

    const jobsArray = Array.isArray(payload.jobs) ? payload.jobs : [];
    const jobs = jobsArray
        .map((j) => ({
            title: String(j.title || "").trim(),
            categoryKey: j.categoryKey || null,
            subcategoryKey: j.subcategoryKey || null,
            experienceYears:
                typeof j.experienceYears === "number" ? j.experienceYears : null,
        }))
        .filter(
            (j) =>
                j.title ||
                j.categoryKey ||
                j.subcategoryKey ||
                (j.experienceYears !== null && j.experienceYears !== undefined)
        )
        .slice(0, 20);

    const primaryJob = jobs[0] || {
        title: payload.jobTitle || "",
        categoryKey: payload.categoryKey || null,
        subcategoryKey: payload.subcategoryKey || null,
    };

    const docPayload = {
        uid: null,
        empId,

        // Personal
        firstName: payload.firstName || "",
        lastName: payload.lastName || "",
        fatherName: payload.fatherName || "",
        address: payload.address || "",
        mobile: payload.mobile || "",
        aadhaar: payload.aadhaar || "",
        pan: payload.pan || "",
        ids: Array.isArray(payload.ids) ? payload.ids.slice(0, 10) : [],
        email: payload.email || null,

        // DOB / Age / Experience
        dateOfBirth: dob,
        ageYears: ageYears ?? null,
        experienceYears:
            typeof payload.experienceYears === "number" ? payload.experienceYears : null,

        // Jobs
        jobs,

        // Primary job fields for filtering/grouping
        categoryKey: primaryJob.categoryKey || null,
        subcategoryKey: primaryJob.subcategoryKey || null,

        // Optional
        skills: Array.isArray(payload.skills) ? payload.skills : [],
        bio: payload.bio || "",
        availability: payload.availability || "full-time",

        status: "pending",
        role: "employee",

        createdBy: "self",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };

    const ref = await addDoc(employeesCol, docPayload);
    return ref;
};

/** Update employee record (partial). */
export const updateEmployee = async (id, updates = {}) => {
    const ref = doc(employeesCol, id);
    const mapped = { ...updates };

    // Cleanup undefined
    Object.keys(mapped).forEach((k) => {
        if (mapped[k] === undefined) delete mapped[k];
    });

    // If dateOfBirth provided without ageYears, recompute
    if (mapped.dateOfBirth && mapped.ageYears === undefined) {
        const recomputed = computeAgeYears(mapped.dateOfBirth);
        mapped.ageYears = recomputed ?? null;
    }

    mapped.updatedAt = serverTimestamp();
    await updateDoc(ref, mapped);
};

/** Set employee status: 'pending' | 'approved' | 'rejected' */
export const setEmployeeStatus = async (id, status) => {
    const ref = doc(employeesCol, id);
    await updateDoc(ref, { status, updatedAt: serverTimestamp() });
};

/** Delete employee (Admin only) */
export const deleteEmployee = async (id) => {
    const ref = doc(employeesCol, id);
    await deleteDoc(ref);
};
